import { useTranslation } from "react-i18next";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { formatNumber } from "utils/utils";
import { MonthlyReportDetails } from "./tips.monthlyReportDetails.component";
import { formatHours } from "../logic/tips.format.logic";
import type { MonthlyReportRow } from "./tips.monthlyReports.logic";
import {
  MonthlyExpandButton,
  MonthlyMetric,
  MonthlyMetricLabel,
  MonthlyMetricValue,
  MonthlyReportRowCard,
  MonthlyReportSummaryRow,
  MonthlyWorkerCell,
} from "./tips.monthlyReports.styled";

interface MonthlyReportWorkerRowProps {
  readonly avatarUrl: string | null;
  readonly hourLabel: string;
  readonly isExpanded: boolean;
  readonly locale: string;
  readonly minuteLabel: string;
  readonly row: MonthlyReportRow;
  readonly onToggleExpanded: (workerId: number) => void;
}

export const MonthlyReportWorkerRow = (
  props: MonthlyReportWorkerRowProps,
): JSX.Element => {
  const { t } = useTranslation();

  return (
    <MonthlyReportRowCard>
      <MonthlyReportSummaryRow>
        <MonthlyWorkerCell>
          <AvatarChip
            title={props.row.name}
            subTitle={props.row.role ?? t("MonthlyTipReport.RolePlaceholder")}
            avatarUrl={props.avatarUrl}
            iconName="v2/person"
          />
        </MonthlyWorkerCell>
        <MonthlyMetric>
          <MonthlyMetricValue>
            {formatNumber(props.row.totalTipsAmount)} {t("Tips.Currency")}
          </MonthlyMetricValue>
          <MonthlyMetricLabel>
            {t("MonthlyTipReport.TipsTotal")}
          </MonthlyMetricLabel>
        </MonthlyMetric>
        <MonthlyMetric>
          <MonthlyMetricValue>
            {formatHours({
              hourLabel: props.hourLabel,
              hours: props.row.totalHours,
              minuteLabel: props.minuteLabel,
            })}
          </MonthlyMetricValue>
          <MonthlyMetricLabel>{t("MonthlyTipReport.Hours")}</MonthlyMetricLabel>
        </MonthlyMetric>
        <MonthlyMetric>
          <MonthlyMetricValue>
            {formatNumber(props.row.distributedAmount)} {t("Tips.Currency")}
          </MonthlyMetricValue>
          <MonthlyMetricLabel>
            {t("MonthlyTipReport.DistributedAndLocked")}
          </MonthlyMetricLabel>
        </MonthlyMetric>
        <MonthlyMetric>
          <MonthlyMetricValue>
            {formatNumber(props.row.paidOutAmount)} {t("Tips.Currency")}
          </MonthlyMetricValue>
          <MonthlyMetricLabel>
            {t("MonthlyTipReport.PaidOut")}
          </MonthlyMetricLabel>
        </MonthlyMetric>
        <MonthlyExpandButton
          type="button"
          aria-label={
            props.isExpanded
              ? t("MonthlyTipReport.CollapseWorker")
              : t("MonthlyTipReport.ExpandWorker")
          }
          onClick={() => props.onToggleExpanded(props.row.workerId)}
        >
          <CustomIcon
            name={props.isExpanded ? "v2/up-chevron" : "v2/down-chevron"}
            size="24px"
            color={ColorV2.Blue1}
          />
        </MonthlyExpandButton>
      </MonthlyReportSummaryRow>

      {props.isExpanded && (
        <MonthlyReportDetails
          details={props.row.details}
          hourLabel={props.hourLabel}
          locale={props.locale}
          minuteLabel={props.minuteLabel}
        />
      )}
    </MonthlyReportRowCard>
  );
};
